import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { BarChart3, Clock, RefreshCw, Eye, CheckCircle2, AlertCircle } from 'lucide-react';
import { BRAND_COLORS, BRAND_LABELS } from '@/constants/bluemonk';

const STATUS_CONFIG = {
  'not-started': { bg: 'bg-slate-100', text: 'text-slate-600', icon: Clock, label: 'Not Started' },
  'in-progress': { bg: 'bg-blue-100', text: 'text-blue-700', icon: RefreshCw, label: 'In Progress' },
  'review': { bg: 'bg-purple-100', text: 'text-purple-700', icon: Eye, label: 'In Review' },
  'completed': { bg: 'bg-green-100', text: 'text-green-700', icon: CheckCircle2, label: 'Completed' },
  'blocked': { bg: 'bg-red-100', text: 'text-red-700', icon: AlertCircle, label: 'Blocked' },
};

export const ProgressTab = ({ items }) => {
  if (items.length === 0) {
    return (
      <Card className="p-8 text-center">
        <BarChart3 className="h-12 w-12 text-slate-400 mx-auto mb-4" />
        <p className="text-lg font-medium text-slate-700">Progress tracking coming soon</p>
        <p className="text-slate-500">Your Content Vista team will update progress here</p>
      </Card>
    );
  }

  // Group by brand
  const brandGroups = {};
  items.forEach(item => {
    if (!brandGroups[item.brand]) {
      brandGroups[item.brand] = [];
    }
    brandGroups[item.brand].push(item);
  });

  // Sort items within each group by order
  Object.keys(brandGroups).forEach(brand => {
    brandGroups[brand].sort((a, b) => (a.order || 0) - (b.order || 0));
  });

  const brandOrder = ['renew-circuits', 'bluemonk-group', 'deshwal', 'all'];
  const sortedBrands = Object.keys(brandGroups).sort((a, b) => 
    brandOrder.indexOf(a) - brandOrder.indexOf(b)
  );

  const calculateProgress = (brandItems) => {
    const completed = brandItems.filter(i => i.status === 'completed').length;
    return Math.round((completed / brandItems.length) * 100);
  };

  return (
    <div className="space-y-6">
      {sortedBrands.map(brand => {
        const brandItems = brandGroups[brand];
        const colors = BRAND_COLORS[brand];
        const progress = calculateProgress(brandItems);
        
        return (
          <Card key={brand} className={`overflow-hidden border-l-4 ${colors.border}`}>
            <div className={`p-4 ${colors.bg} border-b`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Badge className={`${colors.badge} text-white`}>
                    {BRAND_LABELS[brand]}
                  </Badge>
                  <span className="font-semibold text-slate-700">
                    {brand === 'renew-circuits' ? 'Renew Circuits' :
                     brand === 'bluemonk-group' ? 'BlueMonk Group' :
                     brand === 'deshwal' ? 'Deshwal' : 'All Brands'}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-32 h-2 bg-slate-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${colors.badge} transition-all`}
                      style={{ width: `${progress}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-slate-600">{progress}%</span>
                </div>
              </div>
            </div>
            
            <div className="p-4">
              <div className="space-y-3">
                {brandItems.map((item, idx) => {
                  const config = STATUS_CONFIG[item.status] || STATUS_CONFIG['not-started'];
                  const StatusIcon = config.icon;
                  
                  return (
                    <div 
                      key={item.id}
                      className={`flex items-center justify-between p-3 rounded-lg ${config.bg}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                          item.status === 'completed' ? 'bg-green-500 text-white' : 'bg-white'
                        }`}>
                          {item.status === 'completed' ? (
                            <CheckCircle2 className="h-4 w-4" />
                          ) : (
                            <span className="text-xs font-medium text-slate-500">{idx + 1}</span>
                          )}
                        </div>
                        <span className={`font-medium ${config.text}`}>{item.title}</span>
                      </div>
                      <Badge className={`${config.bg} ${config.text} border-0`}>
                        <StatusIcon className="h-3 w-3 mr-1" />
                        {config.label}
                      </Badge>
                    </div>
                  );
                })}
              </div>
            </div>
          </Card>
        );
      })}
      
      {/* Legend */}
      <div className="flex items-center justify-center gap-6 text-sm text-slate-500">
        {Object.entries(STATUS_CONFIG).map(([status, config]) => {
          const Icon = config.icon;
          return (
            <div key={status} className="flex items-center gap-1">
              <Icon className={`h-4 w-4 ${config.text}`} />
              <span>{config.label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
};
